
import SimpleUri from '@ms/odsp-utilities/lib/uri/SimpleUri';
import ISpPageContext from '../../interfaces/ISpPageContext';
import { equalsCaseInsensitive as equals } from '@ms/odsp-utilities/lib/string/StringHelper';
import {
    IGetUrlPartsOptions,
    IItemUrlPartsBasic,
    IItemUrlPartsParams,
    ItemUrlPartsBasic,
    ItemUrlHelperBasic,
    removeDelimiter,
    appendDelimiter,
    startsWith
} from './ItemUrlHelperBasic';
import { deserializeQuery } from '@ms/odsp-utilities/lib/navigation/AddressParser';

export * from './ItemUrlHelperBasic';

const idParamKey = 'id';
const listUrlKey = 'listurl';

/**
 * Specifies how the default site relates to the site specified in the item URL.
 */
export enum SiteRelation {
    /** The default site and the item exist on the same site (SPWeb) */
    sameSite = 0,
    /** The default site and the item exist on a different site (SPWeb) */
    crossSite = 1,
    /** Unable to determine if the default site and the item are on different sites. */
    unknown = 2,
}

/**
 * Represents the available URL parts extractable for an item.
 *
 * @export
 * @interface IItemUrlParts
 */
export interface IItemUrlParts extends IItemUrlPartsBasic {
    /**
     * Determines whether or not the item is on a different domain than the current app.
     *
     * @type {boolean}
     */
    isCrossDomain: boolean;

    /**
     * Determines the relation of the target web to the current web.
     *
     * @type {SiteRelation}
     */
    siteRelation: SiteRelation;

    /**
     * Determines whether or not the item is on a different list than the current app.
     *
     * @type {boolean}
     * @memberOf IItemUrlParts
     */
    isCrossList: boolean;
}

/**
 * Backing implementation of the result from a call to ItemUrlHelper.getUrlParts.
 * This class relies heavily on lazy-initialization of values in order to improve performance.
 *
 * @class ItemUrlParts
 * @implements {IItemUrlParts}
 */
export class ItemUrlParts extends ItemUrlPartsBasic implements IItemUrlParts {
    public get isCrossDomain(): boolean {
        return this._getIsCrossDomain();
    }

    public get isCrossList(): boolean {
        return this._getIsCrossList();
    }

    public get siteRelation(): SiteRelation {
        return this._getSiteRelation();
    }

    private _getIsCrossDomain(): boolean {
        let currentAuthority = this._getCurrentAuthority();

        const isCrossDomain = !equals(new SimpleUri(this._getServerUrl()).authority, currentAuthority);

        this._getIsCrossDomain = () => isCrossDomain;

        return isCrossDomain;
    }

    private _getIsCrossList(): boolean {
        const fullListUrl = this._getFullListUrl();
        const defaultFullListUrl = this._getDefaultListUrl();

        const isCrossList = !equals(fullListUrl, defaultFullListUrl);

        this._getIsCrossList = () => isCrossList;

        return isCrossList;
    }

    private _getSiteRelation(): SiteRelation {
        let siteRelation: SiteRelation = SiteRelation.unknown;

        if (this._getIsCrossDomain()) {
            // All cross-domain URLs are to be treated as remote sites.
            siteRelation = SiteRelation.crossSite;
        } else if (this.webUrl !== void 0) {
            // If webUrl is explicitly provided, then it is either the current site or it is remote.
            // No ambiguity.
            if (equals(removeDelimiter(this.defaultFullWebUrl), removeDelimiter(this._getFullWebUrl()))) {
                siteRelation = SiteRelation.sameSite;
            } else {
                siteRelation = SiteRelation.crossSite;
            }
        } else {
            // If webUrl is not explicitly provided, then look at the provided child URL, either listUrl or path.
            const serverRelativeCurrentWebUrl = new SimpleUri(this.defaultFullWebUrl).path;

            // Do this check with listUrl, falling back to path.
            const serverRelativeUrl =
                this._getServerRelativeListUrl() ||
                this._getServerRelativePath();

            if (serverRelativeUrl !== void 0) {
                if (!startsWith(appendDelimiter(serverRelativeUrl), appendDelimiter(serverRelativeCurrentWebUrl))) {
                    // If url doesn't contain default web URL, then it definitely is cross site
                    siteRelation = SiteRelation.crossSite;
                } else {
                    let itemUri = new SimpleUri(removeDelimiter(serverRelativeUrl));
                    let webUri = new SimpleUri(removeDelimiter(serverRelativeCurrentWebUrl));
                    if (itemUri.segments.length - 1 === webUri.segments.length || (itemUri.segments.length - 2 === webUri.segments.length &&
                        itemUri.segments[itemUri.segments.length - 2] === 'Lists')) {
                        // We know that the site contains the default web Url, but we need to check if server-relative URL contains any potential web URL
                        // Example: If default web URL is http://server/engineering and list URL is http://server/engineering/workItems, then
                        // it's safe to assume that they are on the same web.
                        // Lists is a special folder that contains lists--- one can't create a subweb named Lists. So it's safe to assume they're on the same web.
                        siteRelation = SiteRelation.sameSite;
                    } else {
                        // Site is on the same domain and contains default web url, but the item URL is not one-level
                        // under the default web URL so we can't say whether it's on the same site or not.
                        // Example: If default web URL is http://server/marketing/ and list URL is
                        // http://server/marketing/sales/Forecast, we don't know if "sales" is a subsite
                        // of "marketing" or just a folder.
                        siteRelation = SiteRelation.unknown;
                    }
                }
            }
        }

        this._getSiteRelation = () => siteRelation;

        return siteRelation;
    }

    private _getFullWebUrl(): string {
        let fullWebUrl: string;

        const serverUrl = this._getServerUrl();
        const serverRelativeWebUrl = this._getServerRelativeWebUrl();

        if (serverRelativeWebUrl !== void 0) {
            fullWebUrl = `${serverUrl}${serverRelativeWebUrl}`;
        }

        this._getFullWebUrl = () => fullWebUrl;

        return fullWebUrl;
    }
}

export interface IItemUrlHelperParams {
    // Nothing
}

export interface IItemUrlHelperDependencies {
    pageContext: ISpPageContext;
    itemUrlPartsType?: new (params: IItemUrlPartsParams) => IItemUrlParts;
    useLegacyBehavior?: boolean;
}

/**
 * Component which consumes known information about SharePoint item URLs and constructs
 * normalized URLs for use in item keys and API calls.
 *
 * Different SharePoint API methods return URLs to items, lists, and sites with different formats.
 * This component can be used to extract the necessary information from these URLs and construct
 * invariant versions.
 *
 * The logic in this component codifies all the assumptions made about the format of SharePoint URLs
 * by the web layer.
 *
 * Note, URLs passed in as '/' are assumed to mean the server-relative root. These are internally treated
 * as '' but any URL which evaluates back to the server-relative root will be returned as '/'.
 *
 * @export
 * @class ItemUrlHelper
 *
 * @example
 *  import ItemUrlHelper, { resourceKey as itemUrlHelperKey } from '../dataSources/url/odb/ItemUrlHelper';
 *
 *  let itemUrlHelper = this.resources.consume(itemUrlHelperKey);
 *
 *  // Given only a webUrl and a path, extract a list URL and a server-relative URL.
 *  let {
 *      serverRelativeItemUrl,
 *      normalizedListUrl
 *  } = itemUrlHelper.getUrlParts({
 *      path: 'https://contoso.sharepoint.com/teams/finance/Shared_Documents/reports/Yearly.xlsx',
 *      webUrl: '/teams/finance'
 *  });
 *
 *  let itemKey = this._urlDataSource.getKey({
 *      [UrlQueryKeys.idParamKey]: serverRelativeItemUrl,
 *      [UrlQueryKeys.listUrlKey]: normalizedListUrl
 *  });
 */
export class ItemUrlHelper extends ItemUrlHelperBasic {
    constructor(params: IItemUrlHelperParams, dependencies: IItemUrlHelperDependencies) {
        const {
            itemUrlPartsType = ItemUrlParts
        } = dependencies;

        super({
            context: dependencies.pageContext,
            itemUrlPartsType: itemUrlPartsType
        });
    }

    /**
 * Extracts the available URL parts for an item given available information.
 * Any or all of the inputs may be provided, though the outputs may be undefined
 * if insufficient information is provided.
 *
 * @param {IGetUrlPartsOptions} [params={}]
 * @returns {IItemUrlParts}
 */
    public getUrlParts(options?: IGetUrlPartsOptions): IItemUrlParts {
        return super.getUrlParts(options) as IItemUrlParts;
    }

    public getItemUrlParts(key: string): IItemUrlParts {
        const keyParts = deserializeQuery(key);

        return this.getUrlParts({
            path: keyParts[idParamKey],
            listUrl: keyParts[listUrlKey]
        });
    }
}
